// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor.
// =============================================================================
// DeliveryAddressEditModal.jsx — add / edit a shelter delivery address
// =============================================================================
// The draggable edit form for the DFV Services → Delivery Addresses tab. Replaces
// the old inline-expanding form inside DeliveryAddressesModal (retired) — the tab
// now shows the full management list, and Add / Edit opens this clean two-column
// form in a draggable ModalBackdrop.
//
// Props:
//   isNew          - true for "Add", false for "Edit" (title only).
//   addrForm       - the working address object (from useUserAdminHandlers).
//   setAddrForm    - setter for addrForm.
//   deliverySaving - Save in flight.
//   onSave         - commit (saveAddr).
//   onClose        - dismiss without saving.
//
// Address Name is REQUIRED (25 Oct 2026) — Save stays disabled while it is blank;
// see src/services/addressName.ts for the rule and wording.
// =============================================================================

import {
  ADDRESS_NAME_PLACEHOLDER,
  ADDRESS_NAME_REQUIRED_MSG,
  isBlankAddressName,
} from "../../services/addressName";
import { Btn } from "../../utils/ui.jsx";
import { AddressAutocomplete } from "../form/AddressAutocomplete.jsx";
import { AddressNameHelp } from "../form/AddressNameHelp.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function DeliveryAddressEditModal({
  isNew,
  addrForm,
  setAddrForm,
  deliverySaving,
  onSave,
  onClose,
  // The STORED is_default of the address being edited — drives the guardrail below.
  wasDefault,
}) {
  // Guardrail (John, 8 Aug): the DEFAULT delivery address cannot be made unusable
  // (banned / silent / deactivated). Another address must be made the default first.
  const makingUnusable = !!(
    addrForm.banned_address ||
    addrForm.silent_address ||
    addrForm.deactivated
  );
  const defaultConflict = !!wasDefault && makingUnusable;
  // A blank name is refused (no more auto "Delivery N"). The message shows once a
  // street is entered — i.e. the moment Save would otherwise become available.
  const nameBlank = isBlankAddressName(addrForm.address_name);
  const nameMissing = nameBlank && !!addrForm.street;
  return (
    <ModalBackdrop
      portal
      draggable
      dragTitle={isNew ? "Add delivery address" : "Edit delivery address"}
      onClose={onClose}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 12,
        width: "calc(100vw - 40px)",
        maxWidth: 560,
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        // Keep visible so the Google Places dropdown isn't clipped (see CLAUDE.md).
        overflow: "visible",
        fontFamily: "inherit",
      }}
    >
      <div className="p-6">
        <div className="grid grid-cols-2 gap-[10px] mb-[10px]">
          {/* Address Name — REQUIRED, own full-width row (David, 25 Oct 2026): it prints
              on the delivery (Unleashed Delivery Name). Rule + wording: addressName.ts. */}
          <div className="col-span-2">
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Address Name *
            </label>
            <input
              value={addrForm.address_name}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { address_name: e.target.value });
                });
              }}
              aria-describedby="addr-name-help"
              aria-invalid={nameMissing}
              className="w-full py-2 px-3 rounded-[8px] text-[13px] text-zephyr-text bg-white"
              style={{
                fontFamily: "inherit",
                border: nameMissing ? "1.5px solid #C62828" : "1px solid #CFE0DE",
              }}
              placeholder={ADDRESS_NAME_PLACEHOLDER}
            />
            {nameMissing && (
              <div className="text-[12px] font-semibold text-zephyr-red mt-1">
                {ADDRESS_NAME_REQUIRED_MSG}
              </div>
            )}
            <AddressNameHelp id="addr-name-help" />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Street Address *
            </label>
            <AddressAutocomplete
              value={addrForm.street}
              onChange={function (val) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { street: val });
                });
              }}
              onSelect={function (parts) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, {
                    street: parts.street,
                    street2: parts.street2 || f.street2,
                    suburb_city: parts.suburb || f.suburb_city,
                    state: parts.state || f.state,
                    postcode: parts.postcode || f.postcode,
                  });
                });
              }}
              placeholder="Start typing street address..."
              inputStyle={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #CFE0DE",
                fontSize: 13,
                color: "#1A2E2B",
                fontFamily: "inherit",
                background: "#FFFFFF",
              }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Street Address 2
            </label>
            <input
              value={addrForm.street2}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { street2: e.target.value });
                });
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Suburb/City
            </label>
            <input
              value={addrForm.suburb_city}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { suburb_city: e.target.value });
                });
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              State
            </label>
            <input
              value={addrForm.state}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { state: e.target.value });
                });
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div>
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Postcode
            </label>
            <input
              value={addrForm.postcode}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { postcode: e.target.value });
                });
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            />
          </div>
          <div className="col-span-full">
            <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
              Delivery Instructions
            </label>
            <input
              value={addrForm.delivery_instruction}
              onChange={function (e) {
                setAddrForm(function (f) {
                  return Object.assign({}, f, { delivery_instruction: e.target.value });
                });
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
              placeholder="e.g. Buzz intercom, leave at front desk"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-[13px] mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={addrForm.is_default}
            onChange={function (e) {
              setAddrForm(function (f) {
                return Object.assign({}, f, { is_default: e.target.checked });
              });
            }}
          />
          Set as default delivery address
        </label>

        {/* Status & visibility — edit mode only (a NEW address is created known-good).
            All changes here are STAGED and committed on Save (db-146/147). */}
        {!isNew && (
          <div className="border border-zephyr-border rounded-[8px] p-[14px] mb-3 bg-[#F7FBFB]">
            <div className="text-[12px] font-bold text-zephyr-teal-dark uppercase tracking-[0.4px] mb-[10px]">
              Status &amp; visibility
            </div>
            <label className="flex items-start gap-2 text-[13px] mb-[10px] cursor-pointer">
              <input
                type="checkbox"
                checked={!!addrForm.banned_address}
                onChange={function (e) {
                  setAddrForm(function (f) {
                    return Object.assign({}, f, { banned_address: e.target.checked });
                  });
                }}
                className="mt-[3px]"
              />
              <span>
                <span className="font-semibold text-[#B71C1C]">Banned</span> — do not use. Hidden
                from the shelter and blocks approval of dispatched items.
              </span>
            </label>
            <label className="flex items-start gap-2 text-[13px] mb-[10px] cursor-pointer">
              <input
                type="checkbox"
                checked={!!addrForm.silent_address}
                onChange={function (e) {
                  setAddrForm(function (f) {
                    return Object.assign({}, f, { silent_address: e.target.checked });
                  });
                }}
                className="mt-[3px]"
              />
              <span>
                <span className="font-semibold text-[#455A64]">Silent</span> — usable but never
                shown on the shelter list (saving as silent marks it verified + count 3).
              </span>
            </label>
            <label className="flex items-start gap-2 text-[13px] mb-[12px] cursor-pointer">
              <input
                type="checkbox"
                checked={!!addrForm.deactivated}
                onChange={function (e) {
                  setAddrForm(function (f) {
                    return Object.assign({}, f, { deactivated: e.target.checked });
                  });
                }}
                className="mt-[3px]"
              />
              <span>
                <span className="font-semibold text-[#C62828]">Deactivated</span> — removed from use
                (existing requests still display it). Untick to reactivate.
              </span>
            </label>
            <div className="flex items-center gap-2 text-[13px]">
              <span className="font-semibold">Show-on-list count</span>
              <input
                type="number"
                min={0}
                max={3}
                value={addrForm.silent_address ? 3 : (addrForm.use_count ?? 0)}
                disabled={!!addrForm.silent_address}
                onChange={function (e) {
                  const n = Math.max(0, Math.min(3, Number(e.target.value) || 0));
                  setAddrForm(function (f) {
                    return Object.assign({}, f, { use_count: n });
                  });
                }}
                className="w-[64px] py-1 px-2 rounded-[6px] border border-zephyr-border text-[13px] text-center"
                style={{ fontFamily: "inherit" }}
              />
              <span className="text-[12px] text-zephyr-muted">
                of 3 — shows on the shelter New-Request list at 3 (set to 3 to promote now).
              </span>
            </div>
          </div>
        )}
        {defaultConflict && (
          <div
            className="text-[12px] rounded-[6px] p-[10px] mb-3"
            style={{ color: "#7c1d1d", background: "#FDECEA", border: "1px solid #EF9A9A" }}
          >
            <strong>This is the default delivery address.</strong> You can’t ban, silence or
            deactivate the default. Cancel, edit another address and tick “Set as default” (that
            moves the default), then come back and change this one.
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Btn variant="secondary" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={onSave}
            disabled={deliverySaving || !addrForm.street || nameBlank || defaultConflict}
          >
            {deliverySaving ? "Saving..." : "Save Address"}
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
